/**
 * CONSAI — Client Dashboard (Aether Command aesthetic)
 * - Bento grid: 4 feature glass cards with top stripe + label-caps + headline + bullet list + CTA
 * - Below: Neural Insights (col-span-2) + Node Activity timeline
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { GlassCard } from '../components/ui/glass-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  TrendingUp, AlertCircle, CheckCircle2, Info, AlertTriangle, PlusCircle, CreditCard,
  Users, Crown, Clock, QrCode, ChevronRight, Package, ShoppingCart, FileText, Brain
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import AISuggestionsPanel from '../components/AISuggestionsPanel';
import NodeActivity from '../components/NodeActivity';
import Header from '../components/Header';
import WhatsAppSummaryBanner from '../components/WhatsAppSummaryBanner';
import SignupSuccessPopup from '../components/SignupSuccessPopup';
import { getFeatureColor } from '../lib/feature-colors';
import axios from 'axios';

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSignupSuccess, setShowSignupSuccess] = useState(false);
  const [stats, setStats] = useState(null);
  const [defaultQr, setDefaultQr] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showLowStockModal, setShowLowStockModal] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loadingLowStock, setLoadingLowStock] = useState(false);

  useEffect(() => {
    if (location.state?.justSignedUp) {
      setShowSignupSuccess(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/dashboard`, { withCredentials: true });
        setStats(res.data);
      } catch (e) { /* ignore */ }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchDefaultQr = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/qr-codes/default`, { withCredentials: true });
        setDefaultQr(res.data.default_qr);
      } catch (e) { /* ignore */ }
    };
    fetchDefaultQr();
  }, []);

  const fetchLowStockItems = async () => {
    setLoadingLowStock(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inventory?low_stock=true&per_page=50`, { withCredentials: true });
      setLowStockItems(res.data.items || []);
      setShowLowStockModal(true);
    } catch (e) { /* ignore */ }
    setLoadingLowStock(false);
  };

  const getDaysRemaining = () => {
    if (!user?.plan_expiry_date) return 0;
    const expiry = new Date(user.plan_expiry_date);
    return Math.max(0, Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24)));
  };

  const getPlanBadge = () => {
    const plan = user?.current_plan || 'starter';
    if (plan === 'premium') {
      return (
        <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30">
          <Crown className="h-3 w-3 mr-1" />
          {language === 'hi' ? 'प्रीमियम' : 'Premium'}
        </Badge>
      );
    } else if (plan === 'premium_trial') {
      return (
        <Badge className="bg-purple-500/10 text-purple-300 border border-purple-500/30">
          <Clock className="h-3 w-3 mr-1" />
          {language === 'hi' ? `ट्रायल (${getDaysRemaining()} दिन)` : `Trial (${getDaysRemaining()}d)`}
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-500/10 text-blue-300 border border-blue-500/30">
        {language === 'hi' ? 'स्टार्टर' : 'Starter'}
      </Badge>
    );
  };

  const bentoCards = [
    {
      feature: 'sales',
      stripeColor: '#10B981',
      labelCaps: language === 'hi' ? 'आज की बिक्री' : "TODAY'S SALES",
      value: `₹${(stats?.today_sales_total || 0).toLocaleString('en-IN')}`,
      icon: TrendingUp,
      bullets: [
        { icon: CheckCircle2, text: language === 'hi' ? `${stats?.today_sales_count || 0} आज की बिक्रियाँ` : `${stats?.today_sales_count || 0} sales today` },
        { icon: CheckCircle2, text: language === 'hi' ? `₹${((stats?.month_sales_total)||0).toLocaleString('en-IN')} मासिक` : `₹${((stats?.month_sales_total)||0).toLocaleString('en-IN')} this month` },
        { icon: CheckCircle2, text: language === 'hi' ? `${stats?.today_sales_count > 0 ? 'सक्रिय दिन' : 'पहली बिक्री बाकी'}` : `${stats?.today_sales_count > 0 ? 'Active day' : 'Awaiting first sale'}` },
      ],
      cta: { label: language === 'hi' ? 'बिक्री जोड़ें' : 'Add Sale', kind: 'primary', onClick: () => navigate('/sales') },
      testId: 'bento-sales',
    },
    {
      feature: 'credit',
      stripeColor: '#EF4444',
      labelCaps: language === 'hi' ? 'बकाया उधार' : 'PENDING CREDIT',
      value: `₹${(stats?.credit_given || 0).toLocaleString('en-IN')}`,
      icon: CreditCard,
      bullets: [
        { icon: Info, text: language === 'hi' ? `${stats?.overdue_count || 0} खाते 30+ दिन से बकाया` : `${stats?.overdue_count || 0} accounts > 30 days overdue` },
        { icon: Info, text: language === 'hi' ? `₹${((stats?.credit_taken)||0).toLocaleString('en-IN')} लिया गया` : `₹${((stats?.credit_taken)||0).toLocaleString('en-IN')} taken` },
        { icon: Info, text: language === 'hi' ? 'रिमाइंडर भेजने पर विचार करें' : 'Consider sending reminders' },
      ],
      cta: { label: language === 'hi' ? 'खाताबुक देखें' : 'Review Ledger', kind: 'outline', onClick: () => navigate('/credit') },
      testId: 'bento-credit',
    },
    {
      feature: 'stock',
      stripeColor: '#F59E0B',
      labelCaps: language === 'hi' ? 'कम स्टॉक' : 'LOW STOCK',
      value: `${stats?.low_stock_items || 0} ${language === 'hi' ? 'आइटम' : 'items'}`,
      icon: AlertTriangle,
      bullets: [
        { icon: AlertCircle, text: language === 'hi' ? `${stats?.low_stock_items || 0} आइटम कम स्टॉक में` : `${stats?.low_stock_items || 0} items running low` },
        { icon: AlertCircle, text: language === 'hi' ? 'पुनर्भरण की आवश्यकता' : 'Reorder required' },
        { icon: AlertCircle, text: language === 'hi' ? 'अभी विवरण देखें' : 'Tap to view details' },
      ],
      cta: { label: language === 'hi' ? 'अभी रीऑर्डर करें' : 'Priority Reorder', kind: 'feature', color: '#F59E0B', onClick: fetchLowStockItems },
      testId: 'bento-stock',
    },
    {
      feature: 'customer',
      stripeColor: '#06B6D4',
      labelCaps: language === 'hi' ? 'कुल ग्राहक' : 'TOTAL CUSTOMERS',
      value: String(stats?.total_customers || 0),
      icon: Users,
      bullets: [
        { icon: PlusCircle, text: language === 'hi' ? `${stats?.new_customers_month || 0} नए (MTD)` : `${stats?.new_customers_month || 0} new (MTD)` },
        { icon: PlusCircle, text: language === 'hi' ? 'सक्रिय आधार' : 'Active base' },
        { icon: PlusCircle, text: language === 'hi' ? 'CRM समकालिक' : 'CRM synced' },
      ],
      cta: { label: language === 'hi' ? 'ग्राहक देखें' : 'View Audience', kind: 'outline', onClick: () => navigate('/customers') },
      testId: 'bento-customers',
    },
  ];

  const quickActions = [
    { title: language === 'hi' ? 'बिक्री' : 'Sale', icon: ShoppingCart, feature: 'sales', action: () => navigate('/sales') },
    { title: language === 'hi' ? 'ग्राहक' : 'Customer', icon: Users, feature: 'customer', action: () => navigate('/customers') },
    { title: language === 'hi' ? 'स्टॉक' : 'Stock', icon: Package, feature: 'stock', action: () => navigate('/stock') },
    { title: language === 'hi' ? 'इनवॉइस' : 'Invoice', icon: FileText, feature: 'invoice', action: () => navigate('/invoices') },
  ];

  return (
    <div className="min-h-screen aether-bg relative">
      <SignupSuccessPopup show={showSignupSuccess} onClose={() => setShowSignupSuccess(false)} />
      <Sidebar />

      <div className="lg:ml-64 pb-20 lg:pb-8 relative">
        <Header title={t('dashboard')} />

        {/* Mobile header row */}
        <div className="px-4 pt-3 lg:hidden flex justify-between items-center">
          <img src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" alt="CONSAI" className="h-8" />
          <div className="flex items-center gap-2">
            {user?.role === 'team_member' && (
              <Badge className="bg-blue-500/10 text-blue-300 border border-blue-500/30 text-xs">
                <Users className="h-3 w-3 mr-1" />
                {language === 'hi' ? 'टीम' : 'Team'}
              </Badge>
            )}
            {getPlanBadge()}
          </div>
        </div>

        <main className="p-4 md:px-10 md:py-8 space-y-10 md:space-y-12">
          <WhatsAppSummaryBanner />

          {/* Page header (Aether style) */}
          <section className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="label-caps text-white/55 mb-2">{language === 'hi' ? 'इंटेलिजेंस डैशबोर्ड' : 'INTELLIGENCE DASHBOARD'}</p>
              <h1 className="display-md md:display-lg text-white">
                {language === 'hi' ? `नमस्ते, ${user?.name?.split(' ')[0] || 'User'}` : `Welcome back, ${user?.name?.split(' ')[0] || 'User'}`}
              </h1>
              {user?.business_name && (
                <p className="text-white/55 text-sm mt-2">{user.business_name}</p>
              )}
            </div>
            <div className="hidden md:flex items-center gap-2">
              {user?.role === 'team_member' && (
                <Badge className="bg-blue-500/10 text-blue-300 border border-blue-500/30">
                  <Users className="h-3 w-3 mr-1" />
                  {language === 'hi' ? 'टीम सदस्य' : 'Team Member'}
                </Badge>
              )}
              {getPlanBadge()}
              {user?.current_plan !== 'premium' && (
                <Button
                  style={{ backgroundColor: '#FFFFFF', color: '#0A0A0B' }}
                  onClick={() => navigate('/plans')}
                  className="hover:opacity-90 font-bold rounded-lg"
                  data-testid="upgrade-plan-btn"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {user?.current_plan === 'premium_trial' ? (language === 'hi' ? 'प्लान चुनें' : 'Choose Plan') : t('upgrade')}
                </Button>
              )}
            </div>
          </section>

          {/* Bento grid — 4 feature cards */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bentoCards.map((card, idx) => {
              const Icon = card.icon;
              return (
                <GlassCard
                  key={idx}
                  feature={card.feature}
                  stripe={true}
                  className="p-6 md:p-8"
                  data-testid={card.testId}
                >
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <p className="label-caps text-white/55 mb-1">{card.labelCaps}</p>
                      <h3 className="headline-lg text-white">{card.value}</h3>
                    </div>
                    <div
                      className="p-2.5 rounded-lg"
                      style={{ backgroundColor: `${card.stripeColor}1A`, color: card.stripeColor }}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <ul className="space-y-3 mb-7">
                    {card.bullets.map((b, i) => {
                      const BIcon = b.icon;
                      return (
                        <li key={i} className="flex items-center gap-3 text-sm text-white/65">
                          <BIcon className="h-4 w-4 flex-shrink-0" style={{ color: card.stripeColor }} fill="currentColor" fillOpacity={0.15} />
                          <span>{b.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                  {card.cta.kind === 'primary' ? (
                    <button
                      onClick={card.cta.onClick}
                      className="w-full py-3 bg-white text-[#0A0A0B] font-bold rounded-lg text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                      data-testid={`${card.testId}-cta`}
                    >
                      {card.cta.label}
                    </button>
                  ) : card.cta.kind === 'feature' ? (
                    <button
                      onClick={card.cta.onClick}
                      className="w-full py-3 text-white font-bold rounded-lg text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                      style={{ backgroundColor: card.cta.color }}
                      data-testid={`${card.testId}-cta`}
                    >
                      {card.cta.label}
                    </button>
                  ) : (
                    <button
                      onClick={card.cta.onClick}
                      className="w-full py-3 border border-[var(--aether-outline-variant)] text-white font-bold rounded-lg text-sm hover:bg-white/[0.04] hover:border-white/30 transition-all active:scale-[0.98]"
                      data-testid={`${card.testId}-cta`}
                    >
                      {card.cta.label}
                    </button>
                  )}
                </GlassCard>
              );
            })}
          </section>

          {/* Quick payment QR (compact) */}
          {defaultQr && (
            <section>
              <GlassCard hoverable={true} stripe={false} className="p-5 flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-lg overflow-hidden cursor-pointer flex-shrink-0" onClick={() => setShowQrModal(true)}>
                  <img src={defaultQr.qr_image_url} alt={defaultQr.qr_name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1">
                  <p className="label-caps text-[#62FF96] mb-1">{language === 'hi' ? 'त्वरित भुगतान' : 'INSTANT PAY'}</p>
                  <h4 className="font-bold text-white tracking-tight flex items-center gap-2"><QrCode className="h-4 w-4" />{defaultQr.qr_name}</h4>
                </div>
                <button onClick={() => setShowQrModal(true)} className="text-sm font-bold text-white/70 hover:text-white">
                  {language === 'hi' ? 'विस्तार' : 'Expand'} →
                </button>
              </GlassCard>
            </section>
          )}

          {/* Quick actions (mobile-friendly row) */}
          <section className="lg:hidden">
            <h3 className="label-caps text-white/55 mb-3">{language === 'hi' ? 'त्वरित क्रियाएँ' : 'QUICK ACTIONS'}</h3>
            <div className="grid grid-cols-4 gap-3">
              {quickActions.map((a, i) => {
                const fc = getFeatureColor(a.feature);
                return (
                  <button
                    key={i}
                    onClick={a.action}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl border border-[var(--aether-outline-variant)] bg-[var(--aether-glass)] hover:bg-[var(--aether-surface)] transition-all active:scale-[0.98]"
                    data-testid={`quick-action-${i}`}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${fc.hex}1F`, border: `1px solid ${fc.hex}44` }}>
                      <a.icon className="h-5 w-5" style={{ color: fc.hex }} />
                    </div>
                    <span className="text-xs text-white/80 font-medium">{a.title}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Insights + Activity grid */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <AISuggestionsPanel />
            <NodeActivity />
          </section>

          {/* Report CTA */}
          {!user?.report_purchased && (
            <section>
              <GlassCard hoverable={true} stripe={false} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Brain className="h-6 w-6 text-white/70" />
                  </div>
                  <div>
                    <p className="label-caps text-white/55 mb-1">{language === 'hi' ? 'AI रिपोर्ट' : 'AI BUSINESS REPORT'}</p>
                    <h3 className="headline-sm text-white mb-1">{t('getBusinessReport')}</h3>
                    <p className="text-sm text-white/55">{t('reportFeatures')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="display-md text-white">{user?.current_plan === 'premium' ? '₹899' : '₹999'}</p>
                    <p className="label-caps text-white/40">{language === 'hi' ? 'एक बार' : 'ONE-TIME'}</p>
                  </div>
                  <button
                    onClick={() => navigate('/payment?type=report')}
                    className="py-3 px-6 bg-white text-[#0A0A0B] font-bold rounded-lg text-sm transition-all hover:opacity-90 active:scale-[0.98] flex items-center gap-2"
                    data-testid="get-report-btn"
                  >
                    {t('getReport')}<ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </GlassCard>
            </section>
          )}

          <footer className="pt-8 border-t border-white/5 text-xs text-white/40 text-center">
            © 2026 BUFFETISM IT VENTURES PVT LTD
          </footer>
        </main>
      </div>

      <MobileNav />

      {/* QR Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="sm:max-w-sm bg-[#111111] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="text-center">{defaultQr?.qr_name}</DialogTitle>
          </DialogHeader>
          {defaultQr && (
            <div className="flex flex-col items-center">
              <div className="w-64 h-64 bg-white rounded-2xl overflow-hidden">
                <img src={defaultQr.qr_image_url} alt={defaultQr.qr_name} className="w-full h-full object-contain" />
              </div>
              <p className="text-sm text-white/60 mt-3">
                {language === 'hi' ? 'ग्राहक को स्कैन करने दें' : 'Let customer scan to pay'}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Low stock modal */}
      <Dialog open={showLowStockModal} onOpenChange={setShowLowStockModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto bg-[#111111] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              {language === 'hi' ? 'कम स्टॉक आइटम' : 'Low Stock Items'}
            </DialogTitle>
          </DialogHeader>
          {loadingLowStock ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-4 border-white/70 border-t-transparent rounded-full" /></div>
          ) : lowStockItems.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/55">{language === 'hi' ? 'सभी आइटम पर्याप्त स्टॉक में हैं' : 'All items have sufficient stock'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lowStockItems.map((item) => (
                <div key={item.item_id} className="flex items-center justify-between p-3 rounded-lg border border-[var(--aether-outline-variant)] bg-[var(--aether-surface-low)]">
                  <div>
                    <p className="font-medium text-white">{item.item_name}</p>
                    <p className="text-sm text-white/65">
                      {language === 'hi' ? 'स्टॉक' : 'Stock'}: <span className="font-bold text-amber-300">{item.current_quantity} {item.unit}</span>
                    </p>
                    <p className="text-xs text-white/40">{language === 'hi' ? 'सीमा' : 'Threshold'}: {item.low_stock_threshold} {item.unit}</p>
                  </div>
                  <button
                    onClick={() => { setShowLowStockModal(false); navigate('/stock'); }}
                    className="text-xs px-3 py-1.5 border border-[var(--aether-outline-variant)] rounded-lg text-white hover:bg-white/5"
                  >
                    {language === 'hi' ? 'स्टॉक जोड़ें' : 'Add Stock'}
                  </button>
                </div>
              ))}
              <button
                style={{ backgroundColor: '#FFFFFF', color: '#0A0A0B' }}
                className="w-full mt-3 py-2.5 rounded-lg font-bold hover:opacity-90"
                onClick={() => { setShowLowStockModal(false); navigate('/stock?low_stock=true'); }}
              >
                {language === 'hi' ? 'सभी कम स्टॉक देखें' : 'View All Low Stock'}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
